'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { SectionHeader } from '@/components/section-header'
import { Container } from '@/components/container'
import { MotionDiv } from '@/components/motion-wrapper'
import { getAllBlogPosts, BlogPost } from '@/lib/blog-data'
import Link from 'next/link'
import { 
  DocumentTextIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'

const iconMap: Record<string, React.ReactNode> = {
  'MVP Development': <RocketLaunchIcon className="h-6 w-6" />,
  'SaaS': <ChartBarIcon className="h-6 w-6" />,
  'Architecture': <LightBulbIcon className="h-6 w-6" />,
  'AI Integration': <DocumentTextIcon className="h-6 w-6" />,
}

export default function BlogPage() {
  const blogPosts = getAllBlogPosts()
  const [selectedCategory, setSelectedCategory] = useState<string>('All')

  const categories = ['All', ...Array.from(new Set(blogPosts.map(post => post.category)))]
  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory)

  return (
    <div className="overflow-hidden">
      <Container className="py-12 sm:py-16 md:py-24">
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionHeader
            subheading="Blog"
            heading="Resources & Insights"
            description="Practical guides, insights, and best practices from our experience building MVPs and SaaS products."
          />
        </MotionDiv>

        {/* Category Filter */}
        <div className="mt-12 flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Blog Posts Grid */}
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, index) => (
              <BlogPostCard key={post.slug} post={post} index={index} />
            ))}
          </div>
        </motion.div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No blog posts found in this category.</p>
          </div>
        )}
      </Container>
    </div>
  )
}

function BlogPostCard({ post, index }: { post: BlogPost; index: number }) {
  const icon = iconMap[post.category] || <DocumentTextIcon className="h-6 w-6" />

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
    >
      <Link href={`/blog/${post.slug}`} className="group block">
        <div className="group relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-lg transition-all hover:border-gray-300 h-full flex flex-col">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-3 rounded-lg bg-gray-100 text-gray-700 group-hover:bg-gray-200 transition-colors">
              {icon}
            </div>
            <div className="flex-grow">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {post.category}
                </span>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-400">{post.readTime}</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                {post.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {post.description}
              </p>
              <div className="text-xs text-gray-500">
                {new Date(post.publishedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  )
}

